import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack,
  Share,
  Favorite,
  FavoriteBorder,
  CalendarToday,
  Person,
  AccessTime
} from '@mui/icons-material';
import { blogService } from '../services/firebase';
import { Blog } from '../types';
import WhatsAppButton from '../components/Common/WhatsAppButton';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../utils/getTranslatedData';

const BlogDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    if (id) {
      fetchBlog();
    }
  }, [id]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      setError(null);
      if (id) {
        const blogData = await blogService.getBlog(id);
        if (blogData) {
          setBlog(blogData);
        } else {
          setError('Blog yazısı bulunamadı');
        }
      }
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError('Blog yazısı yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    if (navigator.share && blog) {
      navigator.share({
        title: getTranslatedData(blog.title, i18n.language),
        text: getTranslatedData(blog.excerpt, i18n.language) || '',
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      // Toast mesajı gösterilebilir
    }
  };

  const formatDate = (date: any) => {
    // Check if the date is a valid Firestore Timestamp
    if (date && typeof date.toDate === 'function') {
      return date.toDate().toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    // Fallback for other date formats or invalid data
    if (date && !isNaN(new Date(date).getTime())) {
      return new Date(date).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    return ''; // Return empty or a placeholder for invalid dates
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !blog) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" color="error" gutterBottom>
            {error || 'Blog yazısı bulunamadı'}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/blog')}>
            Bloglara Dön
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog ? getTranslatedData(blog.title) : t('nav.blog')} - HatTravel</title>
        <meta name="description" content={blog ? getTranslatedData(blog.excerpt) : ''} />
      </Helmet>
      
      <Container maxWidth="lg" sx={{ 
        maxWidth: '100%', 
        overflow: 'hidden',
        px: { xs: 2, sm: 3, md: 4 }
      }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
            >
              Ana Sayfa
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="/blog"
              onClick={(e) => {
                e.preventDefault();
                navigate('/blog');
              }}
            >
              Blog
            </Link>
            <Typography color="text.primary">{getTranslatedData(blog.title)}</Typography>
          </Breadcrumbs>
        </Box>

        {/* Back Button */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/blog')}
            sx={{ mb: 2 }}
          >
            Bloglara Dön
          </Button>
        </Box>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h2" component="h1" gutterBottom sx={{ 
            fontWeight: 700,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: '100%'
          }}>
            {getTranslatedData(blog.title)}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person color="primary" />
              <Typography variant="body2" color="text.secondary">
                {blog.author || 'HatTravel'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday color="primary" />
              <Typography variant="body2" color="text.secondary">
                {formatDate(blog.publishedAt)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime color="primary" />
              <Typography variant="body2" color="text.secondary">
                {blog.readTime || '5'} dk okuma
              </Typography>
            </Box>
          </Box>

          {/* Tags */}
          {blog.tags && blog.tags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
              {blog.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={getTranslatedData(tag)}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <IconButton onClick={() => setIsFavorite(!isFavorite)}>
              {isFavorite ? <Favorite color="error" /> : <FavoriteBorder />}
            </IconButton>
            <IconButton onClick={handleShare}>
              <Share />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: '2fr 300px' }, 
          gap: 4,
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          {/* Main Content */}
          <Box sx={{ 
            minWidth: 0, 
            maxWidth: '100%', 
            overflow: 'hidden' 
          }}>
            {/* Featured Image */}
            {blog.featuredImage && (
              <Card sx={{ 
                mb: 4, 
                overflow: 'hidden',
                maxWidth: '100%',
                width: '100%'
              }}>
                <CardMedia
                  component="img"
                  image={blog.featuredImage}
                  alt={getTranslatedData(blog.title)}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover'
                  }}
                />
              </Card>
            )}

            {/* Excerpt */}
            {blog.excerpt && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {getTranslatedData(blog.excerpt)}
                </Typography>
              </Box>
            )}

            {/* Content */}
            <Card>
              <CardContent>
                <Box
                  className="blog-content"
                  sx={{
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      color: 'var(--navy-blue)',
                      fontWeight: 600,
                      mt: 4,
                      mb: 2,
                    },
                    '& p': {
                      lineHeight: 1.8,
                      mb: 2,
                      color: '#333'
                    },
                    '& a': {
                      color: 'var(--light-blue-cta)',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    },
                    '& ul, & ol': {
                      pl: 3,
                      mb: 2,
                    },
                    '& li': {
                      mb: 1
                    },
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 2,
                      my: 2
                    },
                    '& blockquote': {
                      borderLeft: '4px solid var(--light-blue-cta)',
                      pl: 2,
                      ml: 0,
                      fontStyle: 'italic',
                      color: 'text.secondary'
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: getTranslatedData(blog.content) }}
                />
              </CardContent>
            </Card>

            {/* Related Tours */}
            {blog.relatedTours && blog.relatedTours.length > 0 && (
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    İlgili Turlar
                  </Typography>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                    gap: 2 
                  }}>
                    {blog.relatedTours.map((tour, index) => (
                      <Card variant="outlined" key={index}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {tour.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {tour.destination} • {tour.duration} gün
                          </Typography>
                          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                            {tour.price.toLocaleString('tr-TR')} ₺
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(`/tours/${tour.id}`)}
                            sx={{ mt: 1 }}
                          >
                            Detayları Gör
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>

          {/* Sidebar */}
          <Box sx={{ 
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            {/* Author Info */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Yazar Hakkında
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {blog.author || 'HatTravel'} ekibi olarak, seyahat tutkunlarına en iyi deneyimleri sunmak için çalışıyoruz.
                </Typography>
              </CardContent>
            </Card>

            {/* Share */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Paylaş
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleShare}
                    startIcon={<Share />}
                  >
                    Paylaş
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setIsFavorite(!isFavorite)}
                    startIcon={isFavorite ? <Favorite color="error" /> : <FavoriteBorder />}
                  >
                    Beğen
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Categories */}
            {blog.category && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Kategori
                  </Typography>
                  <Chip
                    label={getTranslatedData(blog.category, i18n.language)}
                    color="primary"
                    variant="outlined"
                  />
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      </Container>
      
      <WhatsAppButton />
    </>
  );
};

export default BlogDetailPage; 